/**
 * AgentEnablement Page — the switchboard (ENGINEERING-PLAN §7, SPEC §5.4).
 *
 * Agents come online in dependency order (PMM first — its beliefs feed
 * everyone; RevOps second — the measurement gate; execution agents after).
 * Each agent renders its enablement checklist: platform rollout,
 * stress-test doc requirement, prerequisite agents. Blocked agents cannot
 * be toggled — the server enforces the same checks.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  Badge,
  Banner,
  BlockStack,
  Button,
  Card,
  Divider,
  InlineStack,
  Layout,
  Page,
  Spinner,
  Text,
} from '@shopify/polaris';
import type { AgentDomain } from '../types/agentSettings';
import {
  getEnablementBoard,
  updateMasterSwitch,
} from '../services/agentSettingsApi';
import { updateAgentSettings } from '../services/agentSettingsApi';
import type { EnablementBoard, EnablementEntry } from '../types/agentSettings';

function ChecklistItem({
  ok,
  label,
}: {
  ok: boolean;
  label: string;
}) {
  return (
    <InlineStack gap="100" blockAlign="center">
      <Text as="span" variant="bodySm" tone={ok ? 'success' : 'subdued'}>
        {ok ? '✓' : '○'}
      </Text>
      <Text as="span" variant="bodySm" tone={ok ? undefined : 'subdued'}>
        {label}
      </Text>
    </InlineStack>
  );
}

function EnablementRow({
  entry,
  onToggle,
  isSaving,
}: {
  entry: EnablementEntry;
  onToggle: (domain: AgentDomain, enable: boolean) => void;
  isSaving: boolean;
}) {
  const { checklist } = entry;
  return (
    <Card>
      <BlockStack gap="200">
        <InlineStack align="space-between" blockAlign="center">
          <InlineStack gap="200" blockAlign="center">
            <Badge>{`#${entry.order}`}</Badge>
            <Text as="h3" variant="headingSm">
              {entry.display_name}
            </Text>
            <Badge
              tone={
                entry.runnable
                  ? 'success'
                  : entry.enabled
                    ? 'warning'
                    : undefined
              }
            >
              {entry.runnable
                ? 'Enabled'
                : entry.enabled
                  ? 'Enabled — blocked'
                  : 'Off'}
            </Badge>
          </InlineStack>
          {entry.enabled ? (
            <Button
              onClick={() => onToggle(entry.domain, false)}
              loading={isSaving}
            >
              Disable
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={() => onToggle(entry.domain, true)}
              disabled={!entry.can_enable}
              loading={isSaving}
            >
              Enable
            </Button>
          )}
        </InlineStack>

        <BlockStack gap="100">
          <ChecklistItem
            ok={checklist.platform_rollout}
            label="Platform rollout (system definition exists)"
          />
          <ChecklistItem
            ok={checklist.stress_test_doc !== null}
            label={
              checklist.stress_test_doc
                ? `Stress-test doc authored (${checklist.stress_test_doc})`
                : 'Stress-test doc required before enablement — not yet authored'
            }
          />
          {checklist.prerequisites.map(prereq => (
            <ChecklistItem
              key={prereq.domain}
              ok={prereq.enabled}
              label={`Prerequisite enabled: ${prereq.domain.replace(/_/g, ' ')}`}
            />
          ))}
        </BlockStack>

        {entry.enabled && !entry.runnable && entry.blockers.length > 0 && (
          <Text as="p" variant="bodySm" tone="caution">
            Enabled but not actually running: {entry.blockers.join(' · ')}
          </Text>
        )}
        {!entry.enabled && !entry.can_enable && entry.blockers.length > 0 && (
          <Text as="p" variant="bodySm" tone="subdued">
            {entry.blockers.join(' · ')}
          </Text>
        )}
      </BlockStack>
    </Card>
  );
}

export function AgentEnablement() {
  const [board, setBoard] = useState<EnablementBoard | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingDomains, setSavingDomains] = useState<Set<string>>(new Set());
  const [isSavingMaster, setIsSavingMaster] = useState(false);

  const fetchBoard = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getEnablementBoard();
      setBoard(data);
    } catch (err) {
      console.error('Failed to load enablement board:', err);
      setError('Failed to load the enablement board. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBoard();
  }, [fetchBoard]);

  const handleToggle = async (domain: AgentDomain, enable: boolean) => {
    setSavingDomains(prev => new Set(prev).add(domain));
    setError(null);
    try {
      await updateAgentSettings(domain, { enabled: enable });
      await fetchBoard();
    } catch (err) {
      const detail =
        (err as Error & { detail?: string }).detail || 'Please try again.';
      setError(`Could not update ${domain}: ${detail}`);
    } finally {
      setSavingDomains(prev => {
        const next = new Set(prev);
        next.delete(domain);
        return next;
      });
    }
  };

  const handleAutonomyToggle = async (
    flag: 'agents_enabled' | 'tier1_auto_enabled'
  ) => {
    if (!board) return;
    setIsSavingMaster(true);
    setError(null);
    try {
      const next = !board[flag];
      const switches: {
        agents_enabled?: boolean;
        tier1_auto_enabled?: boolean;
      } = { [flag]: next };
      // "Stop agent reasoning" must stop ALL candidacy: Tier-1 autonomy
      // alone keeps the reasoning worker running, so turning the human-gated
      // switch off turns autonomy off with it.
      if (flag === 'agents_enabled' && !next && board.tier1_auto_enabled) {
        switches.tier1_auto_enabled = false;
      }
      const updated = await updateMasterSwitch(switches);
      setBoard(updated);
    } catch (err) {
      const detail =
        (err as Error & { detail?: string }).detail || 'Please try again.';
      setError(`Could not update autonomy: ${detail}`);
    } finally {
      setIsSavingMaster(false);
    }
  };

  return (
    <Page
      title="Agent Enablement"
      subtitle="Agents come online in dependency order — Product Marketing first"
    >
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
            {error && (
              <Banner tone="critical" onDismiss={() => setError(null)}>
                {error}
              </Banner>
            )}

            {isLoading && (
              <InlineStack align="center">
                <Spinner size="large" />
              </InlineStack>
            )}

            {!isLoading && board && (
              <Card>
                <BlockStack gap="200">
                  <InlineStack align="space-between" blockAlign="center">
                    <InlineStack gap="200" blockAlign="center">
                      <Text as="h3" variant="headingSm">
                        Agents (human-gated)
                      </Text>
                      <Badge
                        tone={board.agents_enabled ? 'success' : undefined}
                      >
                        {board.agents_enabled ? 'Running' : 'Off'}
                      </Badge>
                    </InlineStack>
                    <Button
                      variant={board.agents_enabled ? 'secondary' : 'primary'}
                      onClick={() => handleAutonomyToggle('agents_enabled')}
                      loading={isSavingMaster}
                    >
                      {board.agents_enabled
                        ? 'Stop agent reasoning'
                        : 'Start agent reasoning'}
                    </Button>
                  </InlineStack>
                  <Text as="p" variant="bodySm" tone="subdued">
                    Enabled agents investigate and design tests, and every
                    proposal waits for your approval in the inbox. Nothing is
                    executed without a human decision.
                  </Text>
                  <Divider />
                  <InlineStack align="space-between" blockAlign="center">
                    <InlineStack gap="200" blockAlign="center">
                      <Text as="h3" variant="headingSm">
                        Tier-1 autonomy
                      </Text>
                      <Badge
                        tone={board.tier1_auto_enabled ? 'success' : undefined}
                      >
                        {board.tier1_auto_enabled ? 'Running' : 'Off'}
                      </Badge>
                    </InlineStack>
                    <Button
                      variant={
                        board.tier1_auto_enabled ? 'secondary' : 'primary'
                      }
                      onClick={() => handleAutonomyToggle('tier1_auto_enabled')}
                      loading={isSavingMaster}
                    >
                      {board.tier1_auto_enabled
                        ? 'Stop autonomous cycles'
                        : 'Start autonomous cycles'}
                    </Button>
                  </InlineStack>
                  <Text as="p" variant="bodySm" tone="subdued">
                    Opting in lets enabled agents run cheap, reversible Tier-1
                    tests automatically. It never widens the platform tier
                    floors — spend and net-new messaging always require human
                    approval in the inbox.
                  </Text>
                  <Divider />
                  <Text as="p" variant="bodySm" tone="subdued">
                    Each agent must pass its enablement checklist: platform
                    rollout, an authored stress-test doc ("how does this agent
                    calcify, overfit, silo, or optimize a vanity metric — and
                    which engine property prevents it"), and its prerequisite
                    agents enabled.
                  </Text>
                </BlockStack>
              </Card>
            )}

            {!isLoading &&
              board &&
              board.agents.map(entry => (
                <EnablementRow
                  key={entry.domain}
                  entry={entry}
                  onToggle={handleToggle}
                  isSaving={savingDomains.has(entry.domain)}
                />
              ))}
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}

export default AgentEnablement;
